import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveTA } from "@/lib/academic";
import { Card } from "@/components/ui/card";
import {
  Users, GraduationCap, BookOpen, UserCog,
  TrendingUp, CheckCircle2, XCircle, Award,
  Wallet, Receipt, AlertCircle,
  ClipboardCheck, CalendarOff, MinusCircle, CalendarRange,
  ArrowUpCircle, History,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatIDR } from "@/lib/format";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card
      data-testid={testid}
      className={`p-5 md:p-6 border border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="mt-2 font-heading text-3xl font-bold text-slate-900 tabular-nums">{value}</div>
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { active: activeTA } = useActiveTA();
  const [stats, setStats] = useState(null);
  const [pay, setPay] = useState(null);
  const [att, setAtt] = useState(null);
  const [rekapTA, setRekapTA] = useState(null);
  const [loading, setLoading] = useState(true);

  const canSeePembayaran = user?.role === "admin" || user?.role === "sekretaris";
  const canSeeRekapTA = user?.role === "admin" || user?.role === "sekretaris";

  useEffect(() => {
    (async () => {
      try {
        const reqs = [api.get("/dashboard/stats"), api.get("/absensi/dashboard")];
        if (canSeePembayaran) reqs.push(api.get("/dashboard/pembayaran"));
        if (canSeeRekapTA) reqs.push(api.get("/dashboard/rekap-tahun-ajaran"));
        const results = await Promise.all(reqs);
        setStats(results[0].data);
        setAtt(results[1].data);
        let idx = 2;
        if (canSeePembayaran) { setPay(results[idx]?.data); idx++; }
        if (canSeeRekapTA) { setRekapTA(results[idx]?.data); }
      } catch (_e) {
        setStats({});
      } finally {
        setLoading(false);
      }
    })();
  }, [canSeePembayaran, canSeeRekapTA]);

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Dashboard</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
          Assalamu&apos;alaikum
        </h1>
        <p className="text-slate-500 text-sm md:text-base">
          Selamat datang di Sistem Informasi Madrasah Al Islamiyyah Sunan Giri Surabaya
        </p>
        <div className="mt-2 inline-flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 w-fit"
             data-testid="dashboard-holiday-note">
          <CalendarOff className="h-3.5 w-3.5 text-amber-600" />
          Hari Libur Rutin: Selasa &amp; Kamis.
          {att?.holiday_name && (
            <span className="ml-2 font-semibold">Hari ini libur ({att.holiday_name}).</span>
          )}
        </div>
      </div>

      {/* Active Tahun Ajaran Card */}
      <Card className="p-5 border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm" data-testid="dashboard-active-ta">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CalendarRange className="h-5 w-5" />
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tahun Ajaran Aktif</div>
              <div className="font-heading text-2xl font-bold text-emerald-700 mt-0.5">
                {activeTA ? activeTA.tahun_ajaran : "Belum diatur"}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Semester</div>
              <div className="font-heading text-2xl font-bold text-emerald-700 mt-0.5 capitalize">
                {activeTA ? activeTA.semester : "—"}
              </div>
            </div>
          </div>
          {!activeTA && user?.role === "admin" && (
            <a href="/tahun-ajaran" className="text-xs text-emerald-700 font-semibold hover:underline">
              Atur sekarang →
            </a>
          )}
        </div>
      </Card>

      {/* Main stats grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
          <StatCard
            testid="stat-santri-aktif"
            icon={GraduationCap}
            label="Santri Aktif"
            value={stats.total_santri_aktif ?? 0}
            accent="bg-emerald-50 text-emerald-700"
          />
          <StatCard
            testid="stat-kelas"
            icon={BookOpen}
            label="Total Kelas"
            value={stats.total_kelas ?? 0}
            accent="bg-sky-50 text-sky-700"
          />
          <StatCard
            testid="stat-ustadz"
            icon={Users}
            label="Ustadz Aktif"
            value={stats.total_ustadz ?? 0}
            accent="bg-amber-50 text-amber-700"
          />
          {user?.role === "admin" ? (
            <StatCard
              testid="stat-pengguna"
              icon={UserCog}
              label="Pengguna Sistem"
              value={stats.total_pengguna ?? 0}
              accent="bg-violet-50 text-violet-700"
            />
          ) : (
            <StatCard
              testid="stat-santri-lulus"
              icon={Award}
              label="Santri Lulus"
              value={stats.total_santri_lulus ?? 0}
              accent="bg-violet-50 text-violet-700"
            />
          )}
        </div>
      )}

      {/* Secondary cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <Card className="p-5 md:p-6 lg:col-span-2 border border-slate-200 shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status Santri</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Distribusi Status</div>
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              <div className="mt-2 font-heading text-2xl font-bold text-emerald-900 tabular-nums">{stats?.total_santri_aktif ?? 0}</div>
              <div className="text-[11px] text-emerald-800/70 uppercase tracking-wider mt-0.5">Aktif</div>
            </div>
            <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-4">
              <Award className="h-4 w-4 text-violet-700" />
              <div className="mt-2 font-heading text-2xl font-bold text-violet-900 tabular-nums">{stats?.total_santri_lulus ?? 0}</div>
              <div className="text-[11px] text-violet-800/70 uppercase tracking-wider mt-0.5">Lulus</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <XCircle className="h-4 w-4 text-slate-600" />
              <div className="mt-2 font-heading text-2xl font-bold text-slate-900 tabular-nums">{stats?.total_santri_nonaktif ?? 0}</div>
              <div className="text-[11px] text-slate-700/70 uppercase tracking-wider mt-0.5">Nonaktif</div>
            </div>
          </div>
        </Card>

        <Card className="p-5 md:p-6 border border-slate-200 shadow-sm bg-white">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Santri per Kelas</div>
          <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Top Kelas</div>
          <div className="mt-4 space-y-2.5">
            {(stats?.per_kelas || []).slice(0, 6).map((row, idx) => {
              const max = Math.max(...(stats.per_kelas || []).map(r => r.jumlah), 1);
              const pct = Math.round((row.jumlah / max) * 100);
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-slate-700 truncate">{row.kelas}</span>
                    <span className="text-slate-500 tabular-nums">{row.jumlah}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {(!stats?.per_kelas || stats.per_kelas.length === 0) && (
              <div className="text-sm text-slate-400 py-4 text-center">Belum ada data</div>
            )}
          </div>
        </Card>
      </div>

      {/* Absensi Hari Ini */}
      <div className="space-y-4" data-testid="dashboard-absensi">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Absensi</div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mt-0.5">Kehadiran Hari Ini</h2>
          </div>
          <ClipboardCheck className="h-5 w-5 text-emerald-600" />
        </div>
        {att?.holiday_name ? (
          <Card className="p-5 border border-amber-200 bg-amber-50/60 flex items-start gap-3" data-testid="dashboard-att-holiday">
            <CalendarOff className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Hari ini libur — {att.holiday_name}</div>
              <div className="text-sm text-amber-800/80 mt-0.5">Tidak ada absensi pada hari libur rutin (Selasa &amp; Kamis).</div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
            <StatCard testid="att-hadir" icon={CheckCircle2} label="Hadir Hari Ini" value={att?.santri?.hadir ?? 0} accent="bg-emerald-50 text-emerald-700" />
            <StatCard testid="att-izin" icon={MinusCircle} label="Izin" value={att?.santri?.izin ?? 0} accent="bg-sky-50 text-sky-700" />
            <StatCard testid="att-sakit" icon={AlertCircle} label="Sakit" value={att?.santri?.sakit ?? 0} accent="bg-amber-50 text-amber-700" />
            <StatCard testid="att-alfa" icon={XCircle} label="Alfa" value={att?.santri?.alfa ?? 0} accent="bg-red-50 text-red-700" />
          </div>
        )}
      </div>

      {/* Rekap Kenaikan Kelas per Tahun Ajaran */}
      {canSeeRekapTA && (
        <div className="space-y-4" data-testid="dashboard-rekap-ta">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Kenaikan Kelas</div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mt-0.5">Rekap per Tahun Ajaran</h2>
            </div>
            <ArrowUpCircle className="h-5 w-5 text-emerald-600" />
          </div>
          {(!rekapTA || (rekapTA.items || []).length === 0) ? (
            <Card className="p-6 border border-slate-200 bg-white text-center" data-testid="rekap-ta-empty">
              <History className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <div className="text-sm text-slate-500">Belum ada riwayat kenaikan kelas.</div>
              {user?.role === "admin" && (
                <a href="/kenaikan-kelas" className="text-xs text-emerald-700 font-semibold hover:underline mt-2 inline-block">
                  Proses kenaikan kelas sekarang →
                </a>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {(rekapTA.items || []).slice(0, 6).map((row) => (
                <Card key={row.tahun_ajaran} className="p-5 border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all" data-testid={`rekap-ta-${row.tahun_ajaran.replace("/", "-")}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tahun Ajaran</div>
                      <div className="font-heading text-lg font-bold text-slate-900 mt-0.5">{row.tahun_ajaran}</div>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                      <CalendarRange className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2">
                    <div className="text-center rounded-lg bg-emerald-50/70 border border-emerald-100 p-2">
                      <div className="font-heading text-base font-bold text-emerald-800 tabular-nums">{row.naik}</div>
                      <div className="text-[10px] text-emerald-700/80 uppercase tracking-wider mt-0.5">Naik</div>
                    </div>
                    <div className="text-center rounded-lg bg-amber-50/70 border border-amber-100 p-2">
                      <div className="font-heading text-base font-bold text-amber-800 tabular-nums">{row.tetap}</div>
                      <div className="text-[10px] text-amber-700/80 uppercase tracking-wider mt-0.5">Tinggal</div>
                    </div>
                    <div className="text-center rounded-lg bg-sky-50/70 border border-sky-100 p-2">
                      <div className="font-heading text-base font-bold text-sky-800 tabular-nums">{row.lulus}</div>
                      <div className="text-[10px] text-sky-700/80 uppercase tracking-wider mt-0.5">Lulus</div>
                    </div>
                    <div className="text-center rounded-lg bg-slate-100 border border-slate-200 p-2">
                      <div className="font-heading text-base font-bold text-slate-700 tabular-nums">{row.nonaktif}</div>
                      <div className="text-[10px] text-slate-600/80 uppercase tracking-wider mt-0.5">Nonaktif</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-slate-500 text-right">
                    Total diproses: <strong className="text-slate-700">{row.total}</strong>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pembayaran Kitab Section */}
      {canSeePembayaran && (
        <div className="space-y-4" data-testid="dashboard-pembayaran">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Pembayaran Kitab</div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mt-0.5">Ringkasan Keuangan</h2>
            </div>
            <Wallet className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
            <Card data-testid="pay-stat-tagihan" className="p-5 border border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Tagihan</div>
                  <div className="mt-2 font-heading text-2xl font-bold text-slate-900 tabular-nums">{formatIDR(pay?.total_tagihan ?? 0)}</div>
                </div>
                <div className="h-10 w-10 rounded-xl bg-slate-50 text-slate-700 flex items-center justify-center">
                  <Receipt className="h-5 w-5" />
                </div>
              </div>
            </Card>
            <Card data-testid="pay-stat-masuk" className="p-5 border border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pembayaran Masuk</div>
                  <div className="mt-2 font-heading text-2xl font-bold text-emerald-700 tabular-nums">{formatIDR(pay?.total_pembayaran_masuk ?? 0)}</div>
                </div>
                <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Wallet className="h-5 w-5" />
                </div>
              </div>
            </Card>
            <Card data-testid="pay-stat-lunas" className="p-5 border border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Santri Lunas</div>
                  <div className="mt-2 font-heading text-3xl font-bold text-slate-900 tabular-nums">{pay?.santri_lunas ?? 0}</div>
                </div>
                <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              </div>
            </Card>
            <Card data-testid="pay-stat-belum" className="p-5 border border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Belum Lunas</div>
                  <div className="mt-2 font-heading text-3xl font-bold text-slate-900 tabular-nums">{pay?.santri_belum_lunas ?? 0}</div>
                </div>
                <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
